import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  Timestamp,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { MissingItem, MissingItemNotificationSettings } from '../types/missingItem';

const COLLECTION_NAME = 'missingItems';
const SETTINGS_COLLECTION = 'missingItemSettings';

class MissingItemService {
  // Create a new missing item
  async createMissingItem(data: Omit<MissingItem, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      const docRef = await addDoc(collection(db, COLLECTION_NAME), {
        ...data,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        status: 'pending'
      });
      return docRef.id;
    } catch (error) {
      console.error('Error creating missing item:', error);
      throw error;
    }
  }

  // Get all missing items for a customer
  async getMissingItemsByCustomer(customerId: string): Promise<MissingItem[]> {
    try {
      const q = query(
        collection(db, COLLECTION_NAME),
        where('customerId', '==', customerId),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
        acknowledgedAt: doc.data().acknowledgedAt?.toDate(),
        uploadedAt: doc.data().uploadedAt?.toDate(),
        approvedAt: doc.data().approvedAt?.toDate(),
        rejectedAt: doc.data().rejectedAt?.toDate(),
        dueDate: doc.data().dueDate?.toDate()
      } as MissingItem));
    } catch (error) {
      console.error('Error getting missing items:', error);
      throw error;
    }
  }

  // Get all missing items for an organization
  async getMissingItemsByOrganization(organizationId: string): Promise<MissingItem[]> {
    try {
      const q = query(
        collection(db, COLLECTION_NAME),
        where('organizationId', '==', organizationId),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
        acknowledgedAt: doc.data().acknowledgedAt?.toDate(),
        uploadedAt: doc.data().uploadedAt?.toDate(),
        approvedAt: doc.data().approvedAt?.toDate(),
        rejectedAt: doc.data().rejectedAt?.toDate(),
        dueDate: doc.data().dueDate?.toDate()
      } as MissingItem));
    } catch (error) {
      console.error('Error getting missing items:', error);
      throw error;
    }
  }

  // Get pending missing items for a customer
  async getPendingMissingItems(customerId: string): Promise<MissingItem[]> {
    try {
      const q = query(
        collection(db, COLLECTION_NAME),
        where('customerId', '==', customerId),
        where('status', 'in', ['pending', 'acknowledged', 'uploaded']),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
        acknowledgedAt: doc.data().acknowledgedAt?.toDate(),
        uploadedAt: doc.data().uploadedAt?.toDate(),
        dueDate: doc.data().dueDate?.toDate()
      } as MissingItem));
    } catch (error) {
      console.error('Error getting pending missing items:', error);
      throw error;
    }
  }

  // Acknowledge a missing item (customer action)
  async acknowledgeMissingItem(itemId: string, userId: string): Promise<void> {
    try {
      const itemRef = doc(db, COLLECTION_NAME, itemId);
      await updateDoc(itemRef, {
        status: 'acknowledged',
        acknowledgedAt: serverTimestamp(),
        acknowledgedBy: userId,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error acknowledging missing item:', error);
      throw error;
    }
  }

  // Mark as uploaded (customer action)
  async markAsUploaded(itemId: string, userId: string, files: string[]): Promise<void> {
    try {
      const itemRef = doc(db, COLLECTION_NAME, itemId);
      await updateDoc(itemRef, {
        status: 'uploaded',
        uploadedAt: serverTimestamp(),
        uploadedFiles: files,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error marking as uploaded:', error);
      throw error;
    }
  }

  // Approve a missing item (staff action)
  async approveMissingItem(itemId: string, userId: string): Promise<void> {
    try {
      const itemRef = doc(db, COLLECTION_NAME, itemId);
      await updateDoc(itemRef, {
        status: 'approved',
        approvedAt: serverTimestamp(),
        approvedBy: userId,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error approving missing item:', error);
      throw error;
    }
  }

  // Reject a missing item (staff action)
  async rejectMissingItem(itemId: string, userId: string, reason: string): Promise<void> {
    try {
      const itemRef = doc(db, COLLECTION_NAME, itemId);
      await updateDoc(itemRef, {
        status: 'rejected',
        rejectedAt: serverTimestamp(),
        rejectedBy: userId,
        rejectionReason: reason,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error rejecting missing item:', error);
      throw error;
    }
  }

  // Update missing item
  async updateMissingItem(itemId: string, data: Partial<MissingItem>): Promise<void> {
    try {
      const itemRef = doc(db, COLLECTION_NAME, itemId);
      await updateDoc(itemRef, {
        ...data,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating missing item:', error);
      throw error;
    }
  }

  // Delete missing item
  async deleteMissingItem(itemId: string): Promise<void> {
    try {
      await deleteDoc(doc(db, COLLECTION_NAME, itemId));
    } catch (error) {
      console.error('Error deleting missing item:', error);
      throw error;
    }
  }

  // Get notification settings
  async getNotificationSettings(organizationId: string): Promise<MissingItemNotificationSettings | null> {
    try {
      const docRef = doc(db, SETTINGS_COLLECTION, organizationId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return {
          ...docSnap.data(),
          lastSentAt: docSnap.data().lastSentAt?.toDate()
        } as MissingItemNotificationSettings;
      }
      return null;
    } catch (error) {
      console.error('Error getting notification settings:', error);
      throw error;
    }
  }

  // Update notification settings
  async updateNotificationSettings(settings: MissingItemNotificationSettings): Promise<void> {
    try {
      const docRef = doc(db, SETTINGS_COLLECTION, settings.organizationId);
      await updateDoc(docRef, { ...settings });
    } catch (error) {
      console.error('Error updating notification settings:', error);
      throw error;
    }
  }

  // Get count of pending items for a customer
  async getPendingCount(customerId: string): Promise<number> {
    try {
      const items = await this.getPendingMissingItems(customerId);
      return items.length;
    } catch (error) {
      console.error('Error getting pending count:', error);
      return 0;
    }
  }
}

export const missingItemService = new MissingItemService();
